import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const LearningRoadmap = () => {
  const [selectedQuarter, setSelectedQuarter] = useState('Q1 2025');

  const roadmapData = {
    'Q1 2025': {
      theme: "AI & Machine Learning Integration",
      focus: "Expanding into AI-powered web applications",
      technologies: [
        {
          name: "TensorFlow.js",
          category: "Machine Learning",
          priority: "High",
          progress: 25,
          startDate: "2025-01-15",
          targetDate: "2025-03-30",
          description: "Client-side machine learning for web applications",
          resources: ["Official Documentation", "Google AI Course", "Practical Projects"],
          milestones: [
            { name: "Basic Model Training", completed: true },
            { name: "Web Integration", completed: false },
            { name: "Production Deployment", completed: false }
          ]
        },
        {
          name: "OpenAI API",
          category: "AI Integration",
          priority: "High",
          progress: 60,
          startDate: "2025-01-01",
          targetDate: "2025-02-28",
          description: "Integrating GPT models into web applications",
          resources: ["OpenAI Documentation", "API Best Practices", "Cost Optimization"],
          milestones: [
            { name: "API Integration", completed: true },
            { name: "Prompt Engineering", completed: true },
            { name: "Production Scaling", completed: false }
          ]
        },
        {
          name: "Langchain",
          category: "AI Framework",
          priority: "Medium",
          progress: 15,
          startDate: "2025-02-01",
          targetDate: "2025-03-31",
          description: "Building complex AI applications with chains",
          resources: ["Langchain Docs", "Community Examples", "Advanced Patterns"],
          milestones: [
            { name: "Basic Chains", completed: false },
            { name: "Custom Agents", completed: false },
            { name: "Production Ready", completed: false }
          ]
        }
      ]
    },
    'Q2 2025': {
      theme: "Advanced Cloud & DevOps",
      focus: "Mastering cloud-native architectures and advanced DevOps practices",
      technologies: [
        {
          name: "Terraform",
          category: "Infrastructure as Code",
          priority: "High",
          progress: 0,
          startDate: "2025-04-01",
          targetDate: "2025-05-31",
          description: "Infrastructure automation and management",
          resources: ["HashiCorp Learn", "AWS Provider Docs", "Best Practices Guide"],
          milestones: [
            { name: "Basic Resources", completed: false },
            { name: "Modules & State", completed: false },
            { name: "Production Workflows", completed: false }
          ]
        },
        {
          name: "Istio Service Mesh",
          category: "Microservices",
          priority: "Medium",
          progress: 0,
          startDate: "2025-04-15",
          targetDate: "2025-06-30",
          description: "Advanced microservices communication and security",
          resources: ["Istio Documentation", "Service Mesh Patterns", "Security Best Practices"],
          milestones: [
            { name: "Basic Setup", completed: false },
            { name: "Traffic Management", completed: false },
            { name: "Security Policies", completed: false }
          ]
        },
        {
          name: "Prometheus & Grafana",
          category: "Monitoring",
          priority: "High",
          progress: 0,
          startDate: "2025-05-01",
          targetDate: "2025-06-15",
          description: "Advanced monitoring and observability",
          resources: ["Prometheus Docs", "Grafana Tutorials", "SRE Practices"],
          milestones: [
            { name: "Metrics Collection", completed: false },
            { name: "Custom Dashboards", completed: false },
            { name: "Alerting Rules", completed: false }
          ]
        }
      ]
    },
    'Q3 2025': {
      theme: "Mobile & Cross-Platform",
      focus: "Expanding to mobile development and cross-platform solutions",
      technologies: [
        {
          name: "React Native",
          category: "Mobile Development",
          priority: "High",
          progress: 0,
          startDate: "2025-07-01",
          targetDate: "2025-08-31",
          description: "Cross-platform mobile app development",
          resources: ["React Native Docs", "Expo Documentation", "Mobile Design Patterns"],
          milestones: [
            { name: "Basic App Structure", completed: false },
            { name: "Native Modules", completed: false },
            { name: "App Store Deployment", completed: false }
          ]
        },
        {
          name: "Flutter",
          category: "Mobile Development",
          priority: "Medium",
          progress: 0,
          startDate: "2025-08-01",
          targetDate: "2025-09-30",
          description: "Google's UI toolkit for mobile, web, and desktop",
          resources: ["Flutter Documentation", "Dart Language Guide", "Widget Catalog"],
          milestones: [
            { name: "Dart Fundamentals", completed: false },
            { name: "Widget Development", completed: false },
            { name: "State Management", completed: false }
          ]
        },
        {
          name: "Electron",
          category: "Desktop Development",
          priority: "Low",
          progress: 0,
          startDate: "2025-09-01",
          targetDate: "2025-09-30",
          description: "Cross-platform desktop applications with web technologies",
          resources: ["Electron Docs", "Desktop Patterns", "Performance Optimization"],
          milestones: [
            { name: "Basic Desktop App", completed: false },
            { name: "Native Integration", completed: false },
            { name: "Distribution", completed: false }
          ]
        }
      ]
    },
    'Q4 2025': {
      theme: "Emerging Technologies",
      focus: "Exploring cutting-edge technologies and future trends",
      technologies: [
        {
          name: "WebAssembly (WASM)",
          category: "Performance",
          priority: "Medium",
          progress: 0,
          startDate: "2025-10-01",
          targetDate: "2025-11-30",
          description: "High-performance web applications with near-native speed",
          resources: ["WebAssembly Docs", "Rust for WASM", "Performance Benchmarks"],
          milestones: [
            { name: "Basic WASM Module", completed: false },
            { name: "JavaScript Integration", completed: false },
            { name: "Production Optimization", completed: false }
          ]
        },
        {
          name: "Web3 & Blockchain",
          category: "Blockchain",
          priority: "Low",
          progress: 0,
          startDate: "2025-11-01",
          targetDate: "2025-12-31",
          description: "Decentralized applications and smart contracts",
          resources: ["Ethereum Docs", "Solidity Tutorial", "Web3.js Guide"],
          milestones: [
            { name: "Smart Contract Basics", completed: false },
            { name: "DApp Development", completed: false },
            { name: "Deployment & Testing", completed: false }
          ]
        },
        {
          name: "Rust",
          category: "Systems Programming",
          priority: "Medium",
          progress: 0,
          startDate: "2025-10-15",
          targetDate: "2025-12-31",
          description: "Systems programming language for performance-critical applications",
          resources: ["Rust Book", "Rustlings Exercises", "Cargo Documentation"],
          milestones: [
            { name: "Language Fundamentals", completed: false },
            { name: "Memory Management", completed: false },
            { name: "Web Backend", completed: false }
          ]
        }
      ]
    }
  };

  const quarters = Object.keys(roadmapData);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'bg-error text-white';
      case 'Medium': return 'bg-warning text-white';
      case 'Low': return 'bg-secondary text-white';
      default: return 'bg-surface text-text-primary';
    }
  };

  const getCategoryIcon = (category) => {
    const iconMap = {
      'Machine Learning': 'Brain',
      'AI Integration': 'Zap',
      'AI Framework': 'Layers',
      'Infrastructure as Code': 'Server',
      'Microservices': 'Network',
      'Monitoring': 'Activity',
      'Mobile Development': 'Smartphone',
      'Desktop Development': 'Monitor',
      'Performance': 'Gauge',
      'Blockchain': 'Link',
      'Systems Programming': 'Cpu'
    };
    return iconMap[category] || 'Code';
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-text-primary mb-4">Learning Roadmap 2025</h2>
        <p className="text-lg text-text-secondary max-w-3xl mx-auto">
          My strategic learning plan for staying ahead of technology trends and continuously expanding 
          my skill set to deliver cutting-edge solutions.
        </p>
      </div>

      {/* Quarter Selector */}
      <div className="flex flex-wrap justify-center gap-2">
        {quarters.map((quarter) => (
          <button
            key={quarter}
            onClick={() => setSelectedQuarter(quarter)}
            className={`px-6 py-3 rounded-lg font-medium transition-premium ${
              selectedQuarter === quarter
                ? 'bg-primary text-white shadow-premium'
                : 'bg-surface text-text-secondary hover:bg-surface-hover hover:text-text-primary'
            }`}
          >
            {quarter}
          </button>
        ))}
      </div>

      {/* Selected Quarter Content */}
      <div className="bg-card rounded-xl border border-border shadow-premium p-8">
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-text-primary mb-2">
            {roadmapData[selectedQuarter].theme}
          </h3>
          <p className="text-lg text-text-secondary">
            {roadmapData[selectedQuarter].focus}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {roadmapData[selectedQuarter].technologies.map((tech, index) => (
            <div
              key={index}
              className="bg-surface rounded-xl border border-border p-6 hover:shadow-premium transition-premium hover-lift"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary to-brand-purple rounded-lg flex items-center justify-center">
                    <Icon name={getCategoryIcon(tech.category)} size={24} className="text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-text-primary">{tech.name}</h4>
                    <p className="text-sm text-text-secondary">{tech.category}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(tech.priority)}`}>
                  {tech.priority}
                </span>
              </div>

              <p className="text-sm text-text-muted mb-4">{tech.description}</p>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-text-primary">Progress</span>
                  <span className="text-sm text-text-secondary">{tech.progress}%</span>
                </div>
                <div className="w-full bg-border rounded-full h-2">
                  <div
                    className="h-2 bg-gradient-to-r from-primary to-brand-purple rounded-full transition-all duration-1000"
                    style={{ width: `${tech.progress}%` }}
                  />
                </div>
              </div>

              {/* Timeline */}
              <div className="mb-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-secondary">Start Date:</span>
                  <span className="text-text-primary">
                    {new Date(tech.startDate).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-secondary">Target Date:</span>
                  <span className="text-text-primary">
                    {new Date(tech.targetDate).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </span>
                </div>
              </div>

              {/* Milestones */}
              <div className="mb-4">
                <h5 className="font-medium text-text-primary mb-2">Milestones</h5>
                <div className="space-y-2">
                  {tech.milestones.map((milestone, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <Icon 
                        name={milestone.completed ? 'CheckCircle' : 'Circle'} 
                        size={16} 
                        className={milestone.completed ? 'text-success' : 'text-text-muted'} 
                      />
                      <span className={`text-sm ${milestone.completed ? 'text-text-primary line-through' : 'text-text-secondary'}`}>
                        {milestone.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Resources */}
              <div>
                <h5 className="font-medium text-text-primary mb-2">Learning Resources</h5>
                <div className="flex flex-wrap gap-1">
                  {tech.resources.map((resource, idx) => (
                    <span
                      key={idx}
                      className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full border border-primary/20"
                    >
                      {resource}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Learning Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-card rounded-xl border border-border p-6 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-success to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Target" size={32} className="text-white" />
          </div>
          <h3 className="text-2xl font-bold text-text-primary mb-2">12</h3>
          <p className="text-text-secondary">Technologies Planned</p>
        </div>

        <div className="bg-card rounded-xl border border-border p-6 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-primary to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Clock" size={32} className="text-white" />
          </div>
          <h3 className="text-2xl font-bold text-text-primary mb-2">480</h3>
          <p className="text-text-secondary">Learning Hours Planned</p>
        </div>

        <div className="bg-card rounded-xl border border-border p-6 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-brand-purple to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Award" size={32} className="text-white" />
          </div>
          <h3 className="text-2xl font-bold text-text-primary mb-2">6</h3>
          <p className="text-text-secondary">Certifications Target</p>
        </div>
      </div>
    </div>
  );
};

export default LearningRoadmap;